/**
 * URL validation utility for manual article submission.
 * Provides client-side SSRF protection and URL format validation.
 * @see docs/specifications/2026-01-20-manual-source-phase3-frontend.md Section 6.4
 */

/**
 * Patterns that indicate blocked/internal addresses.
 * Matches localhost, private IPs, and non-http protocols.
 */
const BLOCKED_PATTERNS = [
  /^https?:\/\/localhost/i,
  /^https?:\/\/127\./,
  /^https?:\/\/10\./,
  /^https?:\/\/172\.(1[6-9]|2\d|3[01])\./,
  /^https?:\/\/192\.168\./,
  /^https?:\/\/\[::1\]/,
  /^https?:\/\/0\.0\.0\.0/,
  /^file:/i,
  /^ftp:/i,
];

export interface UrlValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validates a URL for article submission.
 * Checks format, protocol, and SSRF-blocked patterns.
 * @param url - The URL to validate
 * @returns Validation result with optional error message
 */
export function validateArticleUrl(url: string): UrlValidationResult {
  if (!url.trim()) {
    return { isValid: false, error: 'URL jest wymagany' };
  }

  // Check URL format
  try {
    new URL(url);
  } catch {
    return { isValid: false, error: 'Nieprawidłowy format URL' };
  }

  // Check for blocked patterns (SSRF protection)
  for (const pattern of BLOCKED_PATTERNS) {
    if (pattern.test(url)) {
      return {
        isValid: false,
        error: 'URL nie może wskazywać na localhost ani prywatne adresy IP',
      };
    }
  }

  // Check protocol
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return { isValid: false, error: 'URL musi używać protokołu HTTP lub HTTPS' };
  }

  return { isValid: true };
}

/**
 * Client-side check for whether a URL might be blocked.
 * Used for immediate feedback before API call.
 * @param url - The URL to check
 * @returns true if URL appears to be blocked
 */
export function isBlockedUrl(url: string): boolean {
  if (!url) return false;
  return BLOCKED_PATTERNS.some((pattern) => pattern.test(url));
}
